import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { Price } from 'domain/listing/value-objects';
import { formatPrice, CURRENCY_SYMBOL } from './format-price';
import type { SupportedCurrency } from 'utils/i18n-format-currency';
import { LOCALE_COMMON_PATHS, LOCALE_LISTING_PATHS, Namespaces } from 'types/locales';

export interface UsePriceOptions {
  amount: number | null;
  currency: SupportedCurrency;
  locale: string;
  blur?: boolean;
}

export interface UsePriceResult {
  raw: number | null;
  formatted: string;
  hasValue: boolean;
  isBlurred: boolean;
  currencySymbol: string;
  tooltipText: string;
  ariaLabel: string;
}

const BLUR_PLACEHOLDER = '---,---';

export function usePrice(options: UsePriceOptions): UsePriceResult {
  const { amount, currency, locale, blur = false } = options;
  const { t } = useTranslation(Namespaces.COMMON);

  return useMemo(() => {
    const vo = Price.from(amount);
    const currencySymbol = CURRENCY_SYMBOL[currency];

    // Use VO's isPositive - a $0 listing shouldn't display "$0" prominently
    if (!vo.isPositive) {
      const unavailableText = t(LOCALE_COMMON_PATHS.LISTING_PRICE_UNAVAILABLE, {
        defaultValue: 'Price unavailable',
      });
      
      return {
        raw: vo.value,
        formatted: unavailableText,
        hasValue: false,
        isBlurred: false,
        currencySymbol,
        tooltipText: unavailableText,
        ariaLabel: unavailableText,
      };
    }

    if (blur) {
      return {
        raw: vo.value,
        formatted: `${currencySymbol}${BLUR_PLACEHOLDER}`,
        hasValue: true,
        isBlurred: true,
        currencySymbol,
        tooltipText: '',
        ariaLabel: '',
      };
    }

    const formatted = formatPrice(vo, { currency, locale }) ?? '';

    const tooltipText = t(LOCALE_LISTING_PATHS.PRICE_TOOLTIP_LISTED_PRICE_IN_CURRENCY, {
      currency,
      defaultValue: `Listed price in listed location currency (${currency}).`,
    });

    const ariaLabel = t(LOCALE_COMMON_PATHS.LISTING_PRICE_INFORMATION, {
      defaultValue: 'Price information',
    });

    return {
      raw: vo.value,
      formatted,
      hasValue: true,
      isBlurred: false,
      currencySymbol,
      tooltipText,
      ariaLabel,
    };
  }, [amount, currency, locale, blur, t]);
}
